import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import { useIntl } from "react-intl";

import { MESSAGES as BANKING_MESSAGES } from "../../../pages/banking/banking-accounts/message-definitions";
import { uploadProgress } from "../../../pages/banking/banking-accounts/group-details-panel/upload-progress";
import { getStatementCardState } from "../../../pages/banking/banking-accounts/utils";
import { useBankAccountsUploadManager } from "../../../pages/banking/hooks/use-bank-accounts-upload-manager";
import type { ExtractionStatusItem } from "../../../pages/banking/hooks/use-bank-statement-extraction-statuses";
import { useBankStatementExtractionStatuses } from "../../../pages/banking/hooks/use-bank-statement-extraction-statuses";

import { getAirWindowStart } from "./air-statement-coverage";
import type { StatementCoverageRange } from "./air-statement-coverage";
import { getStatementRelevance } from "./air-statement-relevance";
import { getAirStatementSubtitle } from "./air-statement-subtitle";
import { PANEL_MESSAGES } from "./message-definitions";

type RejectedFile = { file: File; errors: readonly { code: string; message: string }[] };

export type AirStatementRow = {
  key: string;
  label: string;
  cardState: "normal" | "refused" | "failed" | "duplicate";
  subtitle?: string;
  errorText?: string;
  extractionFailed?: boolean;
  isLoading: boolean;
  progress: number;
  phase?: "uploading" | "reading";
  onRetry?: () => void;
  onRemove: () => void;
};

const SETTLED_PROGRESS = 100;

const getPeriodKey = (ranges: StatementCoverageRange[]): string | undefined =>
  ranges.length === 0
    ? undefined
    : ranges
        .map(({ start, end }) => `${start}|${end}`)
        .sort()
        .join(";");

const rowKeys = new WeakMap<object, string>();
let rowKeyCount = 0;

const getRowKey = (file: { name: string }): string => {
  let key = rowKeys.get(file);
  if (!key) {
    key = `${file.name}#${rowKeyCount++}`;
    rowKeys.set(file, key);
  }
  return key;
};

const needsAttention = ({ cardState, extractionFailed }: AirStatementRow): boolean =>
  cardState === "failed" || cardState === "refused" || extractionFailed === true;

const isExtractionProcessing = ({ status, exceededMaxPollTime }: ExtractionStatusItem): boolean =>
  !exceededMaxPollTime && status?.extractionState !== "completed" && status?.extractionState !== "failed";

const isExtractionFailed = ({ status, exceededMaxPollTime }: ExtractionStatusItem): boolean =>
  exceededMaxPollTime || status?.extractionState === "failed";

export const useBankStatementAirUploads = (
  customerDomain: string,
  bankData: FundingApplicationSchemas.BankStatementsForAccountAIRSchema,
  now: Dayjs = dayjs()
) => {
  const intl = useIntl();
  const { uploadsState, onFilesAdded, onFileRemoved, onFileRetry } = useBankAccountsUploadManager(customerDomain);

  const addFiles = (acceptedFiles: File[], rejectedFiles?: RejectedFile[]) =>
    onFilesAdded(acceptedFiles, rejectedFiles);

  const confirmedUploads = uploadsState.uploads.flatMap((upload) => (upload.uploadedFile ? [upload.uploadedFile] : []));
  const extractionItems = useBankStatementExtractionStatuses(customerDomain, confirmedUploads);
  const extractionByObjectKey = new Map(extractionItems.map((item) => [item.upload.objectKey, item]));

  const windowStart = getAirWindowStart(bankData.periods);
  const acceptedRanges: StatementCoverageRange[] = [];
  const uploadedObjectKeys: string[] = [];

  const rowsWithPeriods = uploadsState.uploads.map((upload): AirStatementRow & { periodKey?: string } => {
    const row: AirStatementRow & { periodKey?: string } = {
      key: getRowKey(upload.file),
      label: upload.file.name,
      cardState: "normal",
      isLoading: false,
      progress: SETTLED_PROGRESS,
      onRemove: () => onFileRemoved(upload.file, upload.uploadedFile),
    };

    if (upload.status === "processing") {
      return {
        ...row,
        isLoading: true,
        phase: "reading",
        progress: uploadProgress({ status: upload.status }),
        subtitle: intl.formatMessage(BANKING_MESSAGES.statementCategorizingSubtitle),
      };
    }
    if (upload.status === "queued" || upload.status === "uploading") {
      return {
        ...row,
        isLoading: true,
        phase: "uploading",
        progress: uploadProgress({ progress: upload.progress, status: upload.status }),
        subtitle: intl.formatMessage(BANKING_MESSAGES.floatingFilesListUploadingLabel),
      };
    }
    if (upload.status === "failed") {
      return {
        ...row,
        cardState: "failed",
        errorText: upload.error,
        onRetry: () => onFileRetry(upload.file, upload.uploadedFile),
      };
    }
    if (!upload.uploadedFile) {
      return row;
    }

    const { objectKey } = upload.uploadedFile;
    const extraction = extractionByObjectKey.get(objectKey);
    if (!extraction || isExtractionProcessing(extraction)) {
      return {
        ...row,
        isLoading: true,
        phase: "reading",
        progress: uploadProgress({ status: "processing" }),
        subtitle: intl.formatMessage(BANKING_MESSAGES.statementCategorizingSubtitle),
      };
    }
    uploadedObjectKeys.push(objectKey);
    const isRefused =
      getStatementCardState({
        extractionState: extraction.status?.extractionState ?? null,
        extractionFailureReason: extraction.status?.failureReason ?? null,
      }) === "refused";
    if (isRefused) {
      return { ...row, cardState: "refused" };
    }
    if (isExtractionFailed(extraction) || !extraction.status) {
      return { ...row, extractionFailed: true };
    }

    const relevance = getStatementRelevance(extraction.status, bankData, now);
    if (relevance.kind === "accepted") {
      acceptedRanges.push(...relevance.ranges);
      return {
        ...row,
        subtitle: getAirStatementSubtitle(intl, relevance.ranges, { windowStart }),
        periodKey: getPeriodKey(relevance.ranges),
      };
    }
    if (relevance.kind === "outside_period") {
      return {
        ...row,
        subtitle: getAirStatementSubtitle(intl, relevance.ranges, { outsideRequiredPeriod: true }),
      };
    }
    return {
      ...row,
      cardState: "failed",
      errorText: intl.formatMessage(PANEL_MESSAGES.wrongAccountError),
    };
  });

  const originalByPeriod = new Map<string, string>();
  const rows = rowsWithPeriods.map(({ periodKey, ...row }): AirStatementRow => {
    if (periodKey === undefined) {
      return row;
    }
    const original = originalByPeriod.get(periodKey);
    if (original === undefined) {
      originalByPeriod.set(periodKey, row.label);
      return row;
    }
    return {
      ...row,
      cardState: "duplicate",
      subtitle: intl.formatMessage(BANKING_MESSAGES.statementDuplicateSubtitle, { fileName: original }),
    };
  });

  const orderedRows = [...rows].reverse().sort((a, b) => Number(needsAttention(b)) - Number(needsAttention(a)));
  const readingCount = rows.filter((row) => row.isLoading).length;

  return {
    rows: orderedRows,
    onFilesAdded: addFiles,
    acceptedRanges,
    uploadedObjectKeys,
    isBusy: readingCount > 0,
    isReading: readingCount > 0 && rows.every((row) => !row.isLoading || row.phase === "reading"),
    progress: rows.length === 0 ? 0 : Math.round(rows.reduce((sum, row) => sum + row.progress, 0) / rows.length),
  };
};
